import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { appNavigate } from "../../app/actions.web";
import { logout, openLoginModal, setAuthenticated, setUserInfo } from "../../auth/actions";
import { authApi } from "../../auth/api";
import i18next from "../../base/i18n/i18next";
import Icon from "../../base/icons/components/Icon";
import { IconGear } from "../../base/icons/svg";
import { openSettingsDialog } from "../../settings/actions.web";
import { SETTINGS_TABS } from "../../settings/constants";
const LANGUAGES = [
    { code: "fa", name: "فارسی", dir: "rtl" },
    { code: "en", name: "English", dir: "ltr" },
    { code: "ar", name: "العربية", dir: "rtl" },
];
const LandingNavbar = ({ onLoginClick }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState(i18next.language || "fa");
    const [isLoadingAuth, setIsLoadingAuth] = useState(false);
    const [isLoadingLastRoom, setIsLoadingLastRoom] = useState(false);
    const isAuthenticated = useSelector((state) => state["features/auth"]?.isAuthenticated ?? false);
    const userInfo = useSelector((state) => state["features/auth"]?.userInfo);
    const dropdownRef = useRef(null);
    const langDropdownRef = useRef(null);
    // Sync language state with i18next when language changes elsewhere (e.g., footer)
    useEffect(() => {
        const handleLanguageChanged = (lng) => {
            setCurrentLang(lng);
        };
        i18next.on("languageChanged", handleLanguageChanged);
        return () => {
            i18next.off("languageChanged", handleLanguageChanged);
        };
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
            if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
                setLangDropdownOpen(false);
            }
        };
        if (dropdownOpen || langDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen, langDropdownOpen]);
    useEffect(() => {
        const checkAuth = async () => {
            if (authApi.isAuthenticated()) {
                // First, try to get user info from cookie
                const cachedUserInfo = authApi.getUserInfo();
                if (cachedUserInfo) {
                    // Use cached user info immediately
                    dispatch(setUserInfo(cachedUserInfo));
                    dispatch(setAuthenticated(true));
                    setIsLoadingAuth(false);
                    return;
                }
                // If no cached info, fetch from API
                setIsLoadingAuth(true);
                try {
                    const me = await authApi.getMe();
                    dispatch(setUserInfo(me));
                    dispatch(setAuthenticated(true));
                    // Save to cookie for next time
                    authApi.saveUserInfo(me);
                }
                catch (error) {
                    // Don't clear tokens on network error - user might be temporarily disconnected
                    // Only log warning, keep authentication state
                    console.warn("Failed to fetch user info in navbar, but keeping auth state:", error);
                }
                finally {
                    setIsLoadingAuth(false);
                }
            }
        };
        // Only check auth once on mount, not repeatedly
        checkAuth();
    }, [dispatch]);
    const handleLoginClick = useCallback((e) => {
        e.preventDefault();
        if (onLoginClick) {
            onLoginClick();
        }
        else {
            dispatch(openLoginModal());
        }
    }, [onLoginClick, dispatch]);
    const handleDropdownToggle = useCallback(() => {
        setDropdownOpen((prev) => !prev);
    }, []);
    const handleLangDropdownToggle = useCallback(() => {
        setLangDropdownOpen((prev) => !prev);
    }, []);
    const handleLanguageChange = useCallback((langCode, dir) => {
        i18next.changeLanguage(langCode);
        setCurrentLang(langCode);
        document.documentElement.dir = dir;
        document.documentElement.lang = langCode;
        setLangDropdownOpen(false);
    }, []);
    const handleSettingsClick = useCallback(() => {
        dispatch(openSettingsDialog(SETTINGS_TABS.AUDIO, true));
        setDropdownOpen(false);
    }, [dispatch]);
    const handleLogoutClick = useCallback(async () => {
        // ابتدا state رو reset می‌کنیم برای آپدیت سریع UI
        dispatch(logout());
        setDropdownOpen(false);
        // بعد API logout رو صدا می‌زنیم (fire and forget)
        authApi.logout().catch(() => {
            // Ignore logout errors
        });
    }, [dispatch]);
    const handleLastRoomClick = useCallback(async () => {
        setIsLoadingLastRoom(true);
        try {
            const response = await authApi.activeRoom();
            if (response?.roomName) {
                // Set flag to skip prejoin and go directly to conference
                localStorage.setItem("lastRoomAutoJoin", "true");
                dispatch(appNavigate(response.roomName));
            }
        }
        catch (error) {
            console.error("Failed to get active room:", error);
            let errorMessage = "خطا در دریافت اطلاعات اتاق";
            if (error.status === 404) {
                errorMessage = error.data?.message || "شما در حال حاضر در هیچ اتاق فعالی نیستید.";
            }
            else if (error.data?.message) {
                errorMessage = error.data.message;
            }
            toast.error(errorMessage, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                rtl: true,
            });
        }
        finally {
            setIsLoadingLastRoom(false);
        }
    }, [dispatch]);
    const currentLanguage = LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0];
    return (React.createElement("nav", { className: "landing-navbar" },
        React.createElement("div", { className: "landing-navbar__container" },
            React.createElement("div", { className: "landing-navbar__right" },
                React.createElement("div", { className: "landing-navbar__logo" },
                    React.createElement("a", { href: "/" },
                        React.createElement("img", { src: "images/didar-logo.png", alt: "didar", className: "logobox" }))),
                React.createElement("ul", { className: "landing-navbar__links" },
                    React.createElement("li", null,
                        React.createElement("a", { className: window.location.pathname.startsWith("/faq") ? "is-active" : undefined, href: "/faq" }, t("welcomepage.navbar.faq"))))),
            React.createElement("div", { className: "landing-navbar__auth" },
                isAuthenticated && userInfo && (React.createElement("button", { className: "landing-navbar__btn landing-navbar__btn--last-room", disabled: isLoadingLastRoom, onClick: handleLastRoomClick, type: "button" }, isLoadingLastRoom ? (React.createElement("span", { className: "spinner-small-inline" })) : (React.createElement("span", null, t("welcomepage.navbar.lastRoom"))))),
                React.createElement("div", { className: "landing-navbar__lang-menu", ref: langDropdownRef },
                    React.createElement("button", { className: "landing-navbar__lang-btn", onClick: handleLangDropdownToggle, type: "button" },
                        React.createElement("svg", { fill: "currentColor", height: "18", viewBox: "0 0 256 256", width: "18", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm88,104a87.62,87.62,0,0,1-6.4,32.94l-44.7-27.49a15.92,15.92,0,0,0-6.24-2.23l-22.82-3.08a16.11,16.11,0,0,0-16,7.86h-8.72l-3.8-7.86a15.91,15.91,0,0,0-11-8.67l-8-1.73L96.14,104h16.71a16.06,16.06,0,0,0,7.73-2l12.25-6.76a16.62,16.62,0,0,0,3-2.14l26.91-24.34A15.93,15.93,0,0,0,166,65.9a16,16,0,0,0-2.26-2.6L153,53.94A87.93,87.93,0,0,1,216,128ZM40,128a87.53,87.53,0,0,1,8.54-37.8l11.34,9.72a16,16,0,0,0,3.75,2.35l22.47,10.26,4.48,9.29A16,16,0,0,0,104.93,130l2.35.52L114.13,143l-2.61,5.21a16,16,0,0,0,10,22.8l5.21,1.56,1.27,8.87A16,16,0,0,0,136,193.15V214.8A88.2,88.2,0,0,1,40,128Z" })),
                        React.createElement("span", null, currentLanguage.name),
                        React.createElement("svg", { className: `landing-navbar__lang-arrow ${langDropdownOpen ? "active" : ""}`, fill: "currentColor", height: "12", viewBox: "0 0 256 256", width: "12", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" }))),
                    langDropdownOpen && (React.createElement("div", { className: "landing-navbar__lang-dropdown" }, LANGUAGES.map((lang) => (React.createElement("button", { className: `lang-item ${currentLang === lang.code ? "active" : ""}`, key: lang.code, onClick: () => handleLanguageChange(lang.code, lang.dir), type: "button" }, lang.name)))))),
                isLoadingAuth ? (React.createElement("div", { className: "landing-navbar__loading" },
                    React.createElement("div", { className: "spinner-small" }))) : isAuthenticated && userInfo ? (React.createElement("div", { className: "landing-navbar__user-menu", ref: dropdownRef },
                    React.createElement("button", { className: "landing-navbar__user-btn", onClick: handleDropdownToggle, type: "button" },
                        React.createElement("div", { className: "landing-navbar__user-avatar" },
                            React.createElement("p", null, userInfo.username?.slice(0, 2))),
                        React.createElement("div", { className: "landing-navbar__user-info" },
                            React.createElement("span", { className: "user-name" }, userInfo.username),
                            React.createElement("span", { className: "user-phone" }, userInfo.phone.replace("+98", "0"))),
                        React.createElement("div", { className: `landing-navbar__arrowIcon ${dropdownOpen ? "landing-navbar__arrowIcon--active" : ""}` },
                            React.createElement("svg", { fill: "#000000", height: "18", viewBox: "0 0 256 256", width: "18", xmlns: "http://www.w3.org/2000/svg" },
                                React.createElement("path", { d: "M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" })))),
                    dropdownOpen && (React.createElement("div", { className: "landing-navbar__dropdown" },
                        React.createElement("button", { className: "dropdown-item", onClick: handleSettingsClick, type: "button" },
                            React.createElement(Icon, { size: 18, src: IconGear }),
                            React.createElement("span", null, t("settings.title"))),
                        React.createElement("button", { className: "dropdown-item dropdown-item--logout", onClick: handleLogoutClick, type: "button" },
                            React.createElement("svg", { className: "bi bi-box-arrow-right", fill: "currentColor", height: 16, viewBox: "0 0 16 16", width: 16, xmlns: "http://www.w3.org/2000/svg" },
                                React.createElement("path", { d: "M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z", fillRule: "evenodd" }),
                                React.createElement("path", { d: "M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z", fillRule: "evenodd" })),
                            React.createElement("span", null, t("auth.logout"))))))) : (React.createElement("button", { className: "landing-navbar__btn landing-navbar__btn--auth", onClick: handleLoginClick, type: "button" }, t("welcomepage.navbar.loginSignup")))))));
};
export default LandingNavbar;
